import { Poi } from '@workspaces/types'
import type { Environment } from '@workspaces/types'
import { getPoiIconPublicUrl } from '@/services/poi-icons.service'

const { DEFAULT_POI_ICON_FIT, POI_ICON_FITS, isPoiIconFit } = Poi
export { DEFAULT_POI_ICON_FIT, POI_ICON_FITS, isPoiIconFit }

/**
 * Normalizes one stored `poisIcons` value to the current assignment shape.
 * Plans saved before the fit option stored a bare file basename string;
 * those (and assignments with an unknown fit) get DEFAULT_POI_ICON_FIT.
 * Values that carry no usable file name yield null so the entry is dropped.
 */
export function normalizePoiIconAssignment(
  value: unknown,
): Poi.PoiIconAssignment | null {
  if (typeof value === 'string' && value.length > 0) {
    return { fileName: value, fit: DEFAULT_POI_ICON_FIT }
  }

  if (value !== null && typeof value === 'object') {
    const { fileName, fit } = value as { fileName?: unknown; fit?: unknown }
    if (typeof fileName === 'string' && fileName.length > 0) {
      return {
        fileName,
        fit: isPoiIconFit(fit) ? fit : DEFAULT_POI_ICON_FIT,
      }
    }
  }
  return null
}

/**
 * Normalizes a whole stored `poisIcons` mapping (legacy string values and
 * current assignment objects alike) to `Poi.PoisIcons`. Unreadable entries
 * are dropped rather than failing the plan load.
 */
export function normalizePoisIcons(value: unknown): Poi.PoisIcons {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return {}
  }

  const normalized: Poi.PoisIcons = {}
  for (const [selectionName, entry] of Object.entries(value)) {
    const assignment = normalizePoiIconAssignment(entry)
    if (assignment) {
      normalized[selectionName] = assignment
    }
  }
  return normalized
}

/**
 * Normalizes one URL-shaped entry (the shared-plan cloud function response)
 * the same way normalizePoiIconAssignment treats stored assignments: a cloud
 * function deployed before the fit option returns bare URL strings, which get
 * DEFAULT_POI_ICON_FIT, and unknown fits fall back to it too.
 */
export function normalizePoiIconUrlEntry(
  value: unknown,
): Poi.PoiIconUrlEntry | null {
  if (typeof value === 'string' && value.length > 0) {
    return { url: value, fit: DEFAULT_POI_ICON_FIT }
  }

  if (value !== null && typeof value === 'object') {
    const { url, fit } = value as { url?: unknown; fit?: unknown }
    if (typeof url === 'string' && url.length > 0) {
      return { url, fit: isPoiIconFit(fit) ? fit : DEFAULT_POI_ICON_FIT }
    }
  }
  return null
}

/**
 * Public logo URL plus marker fit for every selection the plan has an icon
 * for, keyed by selection name. Selections without one are simply absent, so
 * a caller can test the key instead of asking for a URL that does not exist.
 *
 * Built as a map rather than resolved per row: the icons modal reads it
 * several times per selection while rendering, and the map layers feed it to
 * the logos cache; the URLs only change when the plan's icons or its id do.
 */
export function buildPoiIconUrlsBySelection(
  environment: Environment.EnvironmentResolver,
  planId: string | null,
  poisIcons: Poi.PoisIcons,
): Record<string, Poi.PoiIconUrlEntry> {
  if (!planId) {
    return {}
  }
  return Object.fromEntries(
    Object.entries(poisIcons ?? {})
      .filter(([, assignment]) => Boolean(assignment?.fileName))
      .map(([selectionName, assignment]) => [
        selectionName,
        {
          url: getPoiIconPublicUrl(environment, planId, assignment.fileName),
          fit: assignment.fit,
        },
      ]),
  )
}
